import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { validateMediaFile } from '../../../../media'
import {
  handleMediaGroupSlotFileSelect,
  validateGroupMediaType,
} from './mediaGroupSlotValidation'

export const useMediaGroupSlotActions = ({
  group,
  images,
  originalMedia,
  canManageMedia,
  onAddToSlot,
  onAddLibraryToSlot,
  onReplaceLibraryToSlot,
  onReplaceInSlot,
  onRemoveFromSlot,
  setSlotError: setSlotErrorExternal,
  requiredSlotRatios,
  isGoogleOnly = false,
  isGoogleSelected = false,
  googleExactRatios = [],
}) => {
  const { t } = useTranslation()

  const [localSlotError, setLocalSlotError] = useState(null)
  const hasExternalSlotError = typeof setSlotErrorExternal === 'function'
  const setSlotError = hasExternalSlotError ? setSlotErrorExternal : setLocalSlotError
  const slotError = hasExternalSlotError ? null : localSlotError

  const [isLibraryPickerOpen, setIsLibraryPickerOpen] = useState(false)
  const fileInputRef = useRef(null)
  const pendingActionRef = useRef(null)

  const openFilePicker = (action) => {
    setSlotError(null)
    pendingActionRef.current = action
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    const action = pendingActionRef.current
    pendingActionRef.current = null

    if (!file || !action || !group?.subGroupId) {
      return
    }

    const { error } = await handleMediaGroupSlotFileSelect(file, {
      groupId: group.subGroupId,
      ratio: action.ratio,
      requiredSlotRatios,
      isReplace: action.type === 'replace',
      fileToReplaceId: action.fileId,
      groupImages: images,
      originalMedia,
      originalId: group.originalId,
      onAddToSlot,
      onReplaceInSlot,
      isGoogleOnly,
      isGoogleSelected,
      googleExactRatios,
      t,
    })

    setSlotError(error)
  }

  const handleRemoveSlot = async (fileId) => {
    setSlotError(null)

    try {
      await onRemoveFromSlot?.(fileId)
    } catch (error) {
      setSlotError(error?.message || 'Failed to remove media from slot')
    }
  }

  const openLibraryAdd = (ratio) => {
    if (!canManageMedia) {
      return
    }

    setSlotError(null)
    pendingActionRef.current = { type: 'add', ratio }
    setIsLibraryPickerOpen(true)
  }

  const openLibraryReplace = (ratio, fileId) => {
    if (!canManageMedia) {
      return
    }

    setSlotError(null)
    pendingActionRef.current = { type: 'replace-library', fileId, ratio }
    setIsLibraryPickerOpen(true)
  }

  const handleLibraryApply = (selectedItems) => {
    const selected = selectedItems?.[0]?.item ?? selectedItems?.[0]
    const action = pendingActionRef.current
    pendingActionRef.current = null

    if (!selected || !action || !group?.subGroupId) {
      return
    }

    const formatError = validateGroupMediaType(
      selected,
      {
        groupImages: images,
        originalMedia,
        fileToReplaceId: action.type === 'replace-library' ? action.fileId : null,
      },
      t,
    )

    if (formatError) {
      setSlotError(formatError)
      return
    }

    const meta = selected.meta_data ?? {}
    const width = selected.width ?? meta.width
    const height = selected.height ?? meta.height

    const validationError = validateMediaFile(
      {
        type: selected.type || 'image/jpeg',
        size: meta.size ?? selected.size ?? 0,
        width,
        height,
        fileName: selected.name || selected.filename || 'library-media',
      },
      { isGoogleOnly, isGoogleSelected, googleExactRatios, targetRatio: action.ratio, requiredSlotRatios, t },
    )

    if (validationError) {
      setSlotError(validationError)
      return
    }

    setSlotError(null)

    if (action.type === 'replace-library') {
      try {
        onReplaceLibraryToSlot?.(selected, action.fileId, action.ratio, group.originalId)
      } catch (error) {
        setSlotError(error?.message || 'Failed to replace media from library')
      }
      return
    }

    onAddLibraryToSlot?.(selected, group.subGroupId, action.ratio, group.originalId)
  }

  return {
    slotError,
    isLibraryPickerOpen,
    fileInputRef,
    handleFileChange,
    openFilePicker,
    openLibraryAdd,
    openLibraryReplace,
    closeLibraryPicker: () => setIsLibraryPickerOpen(false),
    handleLibraryApply,
    handleRemoveSlot,
  }
}
